import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import AdminNavbar from '../../component/adminNavbar';
import {
	Box,
	Heading,
	Grid,
	GridItem,
	Stack,
	FormLabel,
	Input,
	FormControl,
	Select,
	Textarea,
	HStack,
	IconButton,
	Text,
	Button,
	Card,
	CardBody,
	Image,
	Checkbox,
	Flex,
	SimpleGrid,
	Divider,
	Badge,
	VStack,
	Spinner,
	Center,
	Alert as ChakraAlert,
	AlertIcon,
	useToast,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalCloseButton,
	ModalBody,
	ModalFooter,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import { getProductByID, fetchStart, updateProduct } from '../../redux/actions/productActions';
import { getAllCategories } from '../../redux/actions/categoryActions';
import { getAllFacilityList } from '../../redux/actions/facilityActions';
import { getAllPriceGroups } from '../../redux/actions/priceGroupActions';
import { connect } from 'react-redux';
import { ViewIcon, AttachmentIcon, CheckIcon } from '@chakra-ui/icons';
import ProductPreviewCard from '../../component/productPreviewCard';
import Alert from '../../component/alert';
import { buildVariantsPreservingPrices } from '../../utils/variantPricing';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const SectionCard = ({ title, children }) => (
	<Card bg="white" shadow="sm" borderRadius="lg" overflow="hidden">
		<Box px={6} py={4} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
			<Heading size="sm" color="gray.700">{title}</Heading>
		</Box>
		<CardBody px={6} py={5}>
			{children}
		</CardBody>
	</Card>
);

const Editproduct = ({
	product,
	getProductByID,
	productLoading,
	fetchStart,
	getAllCategories,
	categories,
	catloading,
	facList,
	facListLoading,
	getAllFacilityList,
	updateProduct,
	priceGroups,
	getAllPriceGroups,
}) => {
	const { product_id } = useParams();
	const toast = useToast();

	useEffect(() => {
		const unloadCallback = (event) => {
			event.preventDefault();
			event.returnValue = '';
			return '';
		};
		window.addEventListener('beforeunload', unloadCallback);
		return () => window.removeEventListener('beforeunload', unloadCallback);
	}, []);

	useEffect(() => {
		fetchStart();
		getProductByID(product_id);
		getAllCategories();
		getAllFacilityList();
		getAllPriceGroups();
	}, [product_id]);

	const [productDetails, setProductDetails] = useState<any>({});
	const [attribute, setAttribute] = useState([]);
	const [addAttribute, setAddAttribute] = useState(false);
	const [variants, setVariants] = useState<any[]>([]);
	const [file, setFile] = useState<File | null>(null);
	const [previewPhoto, setPreviewPhoto] = useState(null);
	const [changingPhoto, setChangingPhoto] = useState(false);
	const [variablePrice, setVariablePrice] = useState(false);
	const [facPrices, setFacPrices] = useState([]);
	const [groupPrices, setGroupPrices] = useState<{ groupId: number; price: string }[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [originalSnapshot, setOriginalSnapshot] = useState<any>(null);
	const [fishbowlUoms, setFishbowlUoms] = useState<{ name: string; abbreviation: string }[]>([]);

	useEffect(() => {
		const loadUoms = async () => {
			try {
				const res = await fetch(`${BASE_URL}/api/fishbowl/uoms`, {
					headers: { 'x-auth-token': localStorage.getItem('x-auth-token') || '' },
				});
				if (!res.ok) return;
				const data = await res.json();
				setFishbowlUoms(data.uoms || []);
			} catch {
				setFishbowlUoms([]);
			}
		};
		loadUoms();
	}, []);

	useEffect(() => {
		if (product) {
			setProductDetails(product.prod);
			let nextFacPrices = [];
			let nextVariablePrice = false;
			if (product.facility && product.facility.length > 0) {
				nextVariablePrice = true;
				nextFacPrices = product.facility.map((p) => ({ facilityId: p.facility_id, price: String(p.price) }));
				setVariablePrice(true);
				setFacPrices(nextFacPrices);
			}
			let nextAttrs = [];
			let nextVariants = [];
			if (product.attri && product.attri.length > 0) {
				nextAttrs = product.attri;
				setAttribute(product.attri);
				setAddAttribute(true);
				if (product.variants && product.variants.length > 0) {
					nextVariants = product.variants.map((v) => {
						const attrIndices = (v.attributes || []).map((va) =>
							product.attri.findIndex((a) => a.attribute_id === va.attribute_id)
						).filter((i) => i !== -1);
						return {
							attrIndices,
							label: v.label || '',
							price: String(v.price ?? ''),
							facilityPrices: v.facilityPrices || {},
							groupPrices: v.groupPrices || {},
							out_of_stock: !!v.out_of_stock,
							fb_number: v.fb_number || '',
							qty_available: v.qty_available ?? null,
							qty_synced_at: v.qty_synced_at || null,
						};
					});
					setVariants(nextVariants);
				}
			}
			let nextGroupPrices = [];
			if (product.groupPrices && product.groupPrices.length > 0) {
				nextGroupPrices = product.groupPrices.map((g) => ({ groupId: g.price_group_id, price: String(g.price) }));
				setGroupPrices(nextGroupPrices);
			}
			setOriginalSnapshot({
				prod: { ...product.prod },
				attribute: nextAttrs,
				variants: nextVariants,
				facPrices: nextFacPrices,
				groupPrices: nextGroupPrices,
				variablePrice: nextVariablePrice,
			});
		}
	}, [product]);

	useEffect(() => {
		return () => {
			if (previewPhoto) URL.revokeObjectURL(previewPhoto);
		};
	}, [previewPhoto]);

	const parentCategories = categories?.filter((c) => !c.parent_id) || [];
	const subcategoriesForSelected = categories?.filter((c) => c.parent_id == productDetails?.category_id) || [];

	const normPrice = (v: any) => {
		const n = parseFloat(String(v ?? ''));
		return Number.isFinite(n) ? n : null;
	};

	const normalizeOverrideMap = (map: Record<string | number, any> | null | undefined) => {
		const out: Record<string, number> = {};
		if (!map || typeof map !== 'object') return out;
		Object.entries(map).forEach(([key, value]) => {
			const price = normPrice(value);
			if (price === null) return;
			out[String(key)] = price;
		});
		return out;
	};

	const normalizeGroupPriceList = (list: { groupId: number; price: string }[] = []) =>
		[...list]
			.map((g) => ({ groupId: Number(g.groupId), price: normPrice(g.price) }))
			.filter((g) => g.price !== null)
			.sort((a, b) => a.groupId - b.groupId);

	const variantKey = (v: any) => (v.label || (v.attrIndices || []).join('-') || '').toString();

	const changes = useMemo(() => {
		if (!originalSnapshot) {
			return {
				fieldChanges: [],
				variantsAdded: [],
				variantsRemoved: [],
				variantsPriceChanged: [],
				variantGroupOverridesChanged: [],
				variantFacOverridesChanged: [],
				hasChanges: !!file,
				photoChanged: !!file,
				attributesChanged: false,
				facPricesChanged: false,
				groupPricesChanged: false,
			};
		}
		const orig = originalSnapshot.prod || {};
		const fields: { label: string; from: string; to: string }[] = [];
		const track = (label: string, from: any, to: any) => {
			const a = String(from ?? '').trim();
			const b = String(to ?? '').trim();
			if (a !== b) fields.push({ label, from: a || '—', to: b || '—' });
		};
		track('Name', orig.name, productDetails.name);
		track('FB Number', orig.fb_number, productDetails.fb_number);
		track('Description', orig.description, productDetails.description);
		track('Base price', orig.price, productDetails.price);
		track('Unit', orig.unit, productDetails.unit);
		track('Qty per unit', orig.quantity_per_unit, productDetails.quantity_per_unit);
		track('Status', orig.status, productDetails.status);
		track('Out of stock', orig.out_of_stock ? 'Yes' : 'No', productDetails.out_of_stock ? 'Yes' : 'No');
		track('Category', orig.category_id, productDetails.category_id);
		track('Subcategory', orig.subcategory_id, productDetails.subcategory_id);

		const origVariants = originalSnapshot.variants || [];
		const origMap = new Map(origVariants.map((v) => [variantKey(v), v]));
		const curMap = new Map(variants.map((v) => [variantKey(v), v]));

		const variantsAdded = variants.filter((v) => !origMap.has(variantKey(v)));
		const variantsRemoved = origVariants.filter((v) => !curMap.has(variantKey(v)));
		const variantsPriceChanged = variants
			.filter((v) => origMap.has(variantKey(v)))
			.map((v) => {
				const prev = origMap.get(variantKey(v));
				const oldPrice = normPrice(prev.price);
				const newPrice = normPrice(v.price);
				if (oldPrice === newPrice) return null;
				return { label: v.label || 'Variant', oldPrice, newPrice };
			})
			.filter(Boolean);

		const variantGroupOverridesChanged: { label: string; groupId: string; oldPrice: number | null; newPrice: number | null }[] = [];
		const variantFacOverridesChanged: { label: string; facilityId: string; oldPrice: number | null; newPrice: number | null }[] = [];

		variants.filter((v) => origMap.has(variantKey(v))).forEach((v) => {
			const prev = origMap.get(variantKey(v));
			const oldGroups = normalizeOverrideMap(prev.groupPrices);
			const newGroups = normalizeOverrideMap(v.groupPrices);
			const groupIds = new Set([...Object.keys(oldGroups), ...Object.keys(newGroups)]);
			groupIds.forEach((groupId) => {
				const oldPrice = oldGroups[groupId] ?? null;
				const newPrice = newGroups[groupId] ?? null;
				if (oldPrice !== newPrice) {
					variantGroupOverridesChanged.push({
						label: v.label || 'Variant',
						groupId,
						oldPrice,
						newPrice,
					});
				}
			});

			const oldFacs = normalizeOverrideMap(prev.facilityPrices);
			const newFacs = normalizeOverrideMap(v.facilityPrices);
			const facIds = new Set([...Object.keys(oldFacs), ...Object.keys(newFacs)]);
			facIds.forEach((facilityId) => {
				const oldPrice = oldFacs[facilityId] ?? null;
				const newPrice = newFacs[facilityId] ?? null;
				if (oldPrice !== newPrice) {
					variantFacOverridesChanged.push({
						label: v.label || 'Variant',
						facilityId,
						oldPrice,
						newPrice,
					});
				}
			});
		});

		const photoChanged = !!file;
		const attributesChanged = JSON.stringify(originalSnapshot.attribute || []) !== JSON.stringify(attribute);
		const productFacPricesChanged = JSON.stringify(originalSnapshot.facPrices || []) !== JSON.stringify(facPrices);
		const productGroupPricesChanged =
			JSON.stringify(normalizeGroupPriceList(originalSnapshot.groupPrices || [])) !==
			JSON.stringify(normalizeGroupPriceList(groupPrices));
		const facPricesChanged = productFacPricesChanged || variantFacOverridesChanged.length > 0;
		const groupPricesChanged = productGroupPricesChanged || variantGroupOverridesChanged.length > 0;
		const hasChanges =
			fields.length > 0 ||
			variantsAdded.length > 0 ||
			variantsRemoved.length > 0 ||
			variantsPriceChanged.length > 0 ||
			variantGroupOverridesChanged.length > 0 ||
			variantFacOverridesChanged.length > 0 ||
			photoChanged ||
			attributesChanged ||
			productFacPricesChanged ||
			productGroupPricesChanged;

		return {
			fieldChanges: fields,
			variantsAdded,
			variantsRemoved,
			variantsPriceChanged,
			variantGroupOverridesChanged,
			variantFacOverridesChanged,
			photoChanged,
			attributesChanged,
			facPricesChanged,
			groupPricesChanged,
			hasChanges,
		};
	}, [originalSnapshot, productDetails, attribute, variants, facPrices, groupPrices, file]);

	const openPreview = (event?) => {
		event?.preventDefault?.();
		if (!productDetails?.name?.trim()) {
			toast({ title: 'Product name is required.', status: 'warning', duration: 2000 });
			return;
		}
		if (!changes.hasChanges) {
			toast({ title: 'No changes to save.', status: 'info', duration: 2000 });
			return;
		}
		setIsPreviewOpen(true);
	};

	const handleConfirmSave = async () => {
		setIsSubmitting(true);
		const formData = new FormData();
		formData.append('name', productDetails.name);
		formData.append('description', productDetails.description || '');
		formData.append('fb_number', productDetails.fb_number || '');
		formData.append('price', productDetails.price);
		formData.append('unit', productDetails.unit || 'each');
		formData.append('quantity_per_unit', productDetails.quantity_per_unit || '1');
		formData.append('category', productDetails.category_id);
		formData.append('subcategory', productDetails.subcategory_id || '');
		formData.append('status', productDetails.status);
		formData.append('out_of_stock', productDetails.out_of_stock ? '1' : '0');
		if (file) {
			formData.append('photo', file);
		} else {
			formData.append('image', productDetails.image || '');
		}
		formData.append('attributes', JSON.stringify(attribute));
		formData.append('fac_prices', JSON.stringify(facPrices));
		formData.append('group_prices', JSON.stringify(variants.length > 0 ? [] : groupPrices.map((g) => ({ groupId: g.groupId, price: parseFloat(g.price) || 0 }))));
		formData.append('variants', JSON.stringify(variants));
		try {
			await updateProduct(product_id, formData);
			setIsPreviewOpen(false);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		openPreview();
	};

	const handleInputChange = (e) => {
		if (e.target.type === 'file') {
			const f = e.target.files[0];
			if (f) {
				setPreviewPhoto(URL.createObjectURL(f));
				setFile(f);
				setChangingPhoto(false);
			}
			return;
		}
		const { id, value } = e.target;
		setProductDetails((prev) => ({ ...prev, [id]: value }));
	};

	const handleChangeAttribute = (index, field, value) => {
		const newAttributes = [...attribute];
		newAttributes[index] = { ...newAttributes[index], [field]: value };
		setAttribute(newAttributes);
	};

	const handleAddAttribute = () => {
		setAttribute((prev) => [...prev, { attribute_value: '', attribute_name: 'Color' }]);
	};

	const handleDeleteAttribute = (index) => {
		setAttribute((prev) => prev.filter((_, i) => i !== index));
		setVariants([]);
	};

	const handlePriceChange = (facilityId, value) => {
		setFacPrices((prev) => {
			if (value === '') return prev.filter((p) => p.facilityId !== facilityId);
			const existing = prev.find((p) => p.facilityId === facilityId);
			if (existing) return prev.map((p) => (p.facilityId === facilityId ? { ...p, price: value } : p));
			return [...prev, { facilityId, price: value }];
		});
	};

	const handleGroupPriceChange = (groupId, value) => {
		setGroupPrices((prev) => {
			const existing = prev.find((g) => g.groupId === groupId);
			if (existing) return prev.map((g) => (g.groupId === groupId ? { ...g, price: value } : g));
			return [...prev, { groupId, price: value }];
		});
	};

	const generateVariants = () => {
		const { variants: next, preservedCount } = buildVariantsPreservingPrices(attribute, variants);
		setVariants(next);
		if (preservedCount > 0) {
			toast({
				title: `Generated ${next.length} variants`,
				description: `Kept pricing on ${preservedCount} matching option(s).`,
				status: 'success',
				duration: 3000,
			});
		} else {
			toast({
				title: `Generated ${next.length} variants`,
				description: 'Set prices for each combination below.',
				status: 'info',
				duration: 3000,
			});
		}
	};

	const handleVariantPriceChange = (vIdx, value) => {
		setVariants((prev) => prev.map((v, i) => i === vIdx ? { ...v, price: value } : v));
	};

	const handleVariantFbNumberChange = (vIdx, value) => {
		setVariants((prev) => prev.map((v, i) => i === vIdx ? { ...v, fb_number: value } : v));
	};

	const handleVariantOutOfStockChange = (vIdx, checked) => {
		setVariants((prev) => prev.map((v, i) => i === vIdx ? { ...v, out_of_stock: checked } : v));
	};

	const handleVariantFacPriceChange = (vIdx, facilityId, value) => {
		setVariants((prev) => prev.map((v, i) => i === vIdx ? {
			...v,
			facilityPrices: { ...v.facilityPrices, [facilityId]: value },
		} : v));
	};

	const handleVariantGroupPriceChange = (vIdx, groupId, value) => {
		setVariants((prev) => prev.map((v, i) => i === vIdx ? {
			...v,
			groupPrices: { ...v.groupPrices, [groupId]: value },
		} : v));
	};

	if (productLoading) {
		return (
			<>
				<Alert />
				<AdminNavbar />
				<Box ml={{ base: 0, md: "250px" }} minHeight="100vh" bg="gray.100">
					<Center h="80vh">
						<Spinner size="xl" color="teal.500" />
					</Center>
				</Box>
			</>
		);
	}

	return (
		<>
			<Alert />
			<AdminNavbar />
			<Box pt={{ base: 16, md: 6 }} px={{ base: 4, md: 6 }} pb={6} ml={{ base: 0, md: "250px" }} bg="gray.100" minHeight="100vh">
				<Flex align="center" justify="space-between" mb={6} mt={8}>
					<Box>
						<Heading size="lg" color="gray.800">Edit Product</Heading>
						<Text fontSize="sm" color="gray.500" mt={1}>Update product details below</Text>
					</Box>
					<Button
						colorScheme="teal"
						leftIcon={<CheckIcon />}
						size="lg"
						type="submit"
						form="edit-product-form"
						isLoading={isSubmitting}
						loadingText="Saving..."
					>
						Preview & Save
					</Button>
				</Flex>

				<Grid templateColumns={{ base: '1fr', md: '2.5fr 1fr' }} gap={4}>
					<GridItem>
						<form id="edit-product-form" onSubmit={handleSubmit}>
							<Stack spacing={4}>
								{/* Basic Information */}
								<SectionCard title="Basic Information">
									<Stack spacing={4}>
										<SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
											<FormControl id="name" isRequired>
												<FormLabel fontSize="sm">Product Name</FormLabel>
												<Input
													value={productDetails.name || ''}
													onChange={handleInputChange}
													placeholder="Product name"
													focusBorderColor="teal.400"
												/>
											</FormControl>
											<FormControl id="fb_number">
												<FormLabel fontSize="sm">FB Number</FormLabel>
												<Input
													value={productDetails.fb_number || ''}
													onChange={handleInputChange}
													placeholder="e.g. FB-12345"
													focusBorderColor="teal.400"
												/>
											</FormControl>
										</SimpleGrid>
										<FormControl id="description">
											<FormLabel fontSize="sm">Description</FormLabel>
											<Textarea
												value={productDetails.description || ''}
												onChange={(e) => setProductDetails((prev) => ({ ...prev, description: e.target.value }))}
												rows={3}
												focusBorderColor="teal.400"
											/>
										</FormControl>
									</Stack>
								</SectionCard>

								{/* Media & Organization */}
								<SectionCard title="Media & Organization">
									<Stack spacing={4}>
										{/* Photo Upload */}
										<FormControl>
											<FormLabel fontSize="sm">Product Photo</FormLabel>
											{!changingPhoto ? (
												<HStack spacing={4}>
													{(previewPhoto || productDetails.image) && (
														<Image
															src={previewPhoto || `${BASE_URL}/api/image?filename=${productDetails.image}`}
															boxSize="64px"
															objectFit="contain"
															borderRadius="md"
															border="1px solid"
															borderColor="gray.200"
															bg="white"
															p={1}
														/>
													)}
													<Button
														leftIcon={<AttachmentIcon />}
														size="sm"
														variant="outline"
														colorScheme="gray"
														onClick={() => setChangingPhoto(true)}
													>
														{productDetails.image || previewPhoto ? 'Change Photo' : 'Upload Photo'}
													</Button>
												</HStack>
											) : (
												<HStack>
													<Input
														type="file"
														id="photo"
														onChange={handleInputChange}
														accept="image/*"
														size="sm"
														focusBorderColor="teal.400"
													/>
													<Button size="sm" variant="ghost" onClick={() => setChangingPhoto(false)}>
														Cancel
													</Button>
												</HStack>
											)}
										</FormControl>

										<SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
											<FormControl id="status" isRequired>
												<FormLabel fontSize="sm">Status</FormLabel>
												<Select
													value={productDetails.status}
													onChange={handleInputChange}
													focusBorderColor="teal.400"
												>
													<option value="1">Active</option>
													<option value="0">Inactive</option>
												</Select>
											</FormControl>
											<FormControl>
												<FormLabel fontSize="sm">Stock</FormLabel>
												<HStack spacing={3} flexWrap="wrap">
													<Badge colorScheme={productDetails.out_of_stock ? 'red' : 'green'} fontSize="sm" px={2} py={1}>
														{productDetails.out_of_stock ? 'Out of Stock' : 'In Stock'}
													</Badge>
													<Checkbox
														isChecked={!!productDetails.out_of_stock}
														onChange={(e) => setProductDetails((prev) => ({ ...prev, out_of_stock: e.target.checked }))}
														colorScheme="red"
													>
														<Text fontSize="sm">Mark as Out of Stock</Text>
													</Checkbox>
												</HStack>
												{!addAttribute && (
													<Box mt={2} p={2} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.100">
														<Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
															Fishbowl qty
														</Text>
														<Text fontSize="sm" fontWeight="semibold" color="gray.800">
															{productDetails.qty_available == null || productDetails.qty_available === ''
																? '—'
																: Number(productDetails.qty_available)}
														</Text>
														<Text fontSize="xs" color="gray.400">
															Synced{' '}
															{productDetails.qty_synced_at
																? new Date(productDetails.qty_synced_at).toLocaleString('en-US', {
																		month: 'short',
																		day: 'numeric',
																		year: 'numeric',
																		hour: '2-digit',
																		minute: '2-digit',
																	})
																: '—'}
														</Text>
													</Box>
												)}
											</FormControl>
											<FormControl id="category_id" isRequired>
												<FormLabel fontSize="sm">Category</FormLabel>
												<Select
													value={productDetails.category_id || ''}
													isDisabled={catloading}
													onChange={handleInputChange}
													focusBorderColor="teal.400"
												>
													{parentCategories.map((cat) => (
														<option key={cat.category_id} value={cat.category_id}>
															{cat.category_name}
														</option>
													))}
												</Select>
											</FormControl>
										</SimpleGrid>

										{subcategoriesForSelected.length > 0 && (
											<FormControl id="subcategory_id">
												<FormLabel fontSize="sm">Subcategory</FormLabel>
												<Select
													placeholder="Select subcategory (optional)"
													value={productDetails.subcategory_id || ''}
													onChange={handleInputChange}
													focusBorderColor="teal.400"
												>
													{subcategoriesForSelected.map((cat) => (
														<option key={cat.category_id} value={cat.category_id}>
															{cat.category_name}
														</option>
													))}
												</Select>
											</FormControl>
										)}
									</Stack>
								</SectionCard>

								{/* Inventory & Pricing */}
								<SectionCard title="Inventory & Pricing">
									<SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
										<FormControl id="price">
											<FormLabel fontSize="sm">Price ($)</FormLabel>
											<Input
												type="number"
												step="0.01"
												value={productDetails.price || ''}
												onChange={handleInputChange}
												focusBorderColor="teal.400"
												onWheel={(e) => e.currentTarget.blur()}
											/>
										</FormControl>
										<FormControl id="unit" isRequired>
											<FormLabel fontSize="sm">Unit of Measurement</FormLabel>
											<Select
												value={productDetails.unit || ''}
												onChange={handleInputChange}
												focusBorderColor="teal.400"
												placeholder={fishbowlUoms.length ? 'Select Fishbowl UOM' : 'Sync UOMs from Fishbowl first'}
											>
												{productDetails.unit &&
													!fishbowlUoms.some(
														(u) => u.abbreviation === productDetails.unit || u.name === productDetails.unit
													) && (
														<option value={productDetails.unit}>
															{productDetails.unit} (current)
														</option>
													)}
												{fishbowlUoms.map((u) => (
													<option key={u.abbreviation} value={u.abbreviation}>
														{u.name} ({u.abbreviation})
													</option>
												))}
											</Select>
										</FormControl>
										<FormControl id="quantity_per_unit" isRequired>
											<FormLabel fontSize="sm">Quantity Per Unit</FormLabel>
											<Input
												type="number"
												min="1"
												value={productDetails.quantity_per_unit || ''}
												onChange={handleInputChange}
												focusBorderColor="teal.400"
												onWheel={(e) => e.currentTarget.blur()}
											/>
										</FormControl>
									</SimpleGrid>
								</SectionCard>

								{/* Advanced Options */}
								<SectionCard title="Advanced Options">
									<Stack spacing={4}>
										<Checkbox colorScheme="teal" isChecked={addAttribute} onChange={() => {
											if (!addAttribute) handleAddAttribute();
											else { setAttribute([]); setVariants([]); }
											setAddAttribute(!addAttribute);
										}}>
											<Text fontSize="sm">This product has multiple options (e.g. size, color)</Text>
										</Checkbox>

										{addAttribute && (
											<Box p={4} borderWidth="1px" borderRadius="md" borderColor="gray.200" bg="gray.50">
												<Stack spacing={3}>
													<Text fontSize="xs" color="gray.500">Define option types and values. Then click Generate Variants to set a price per combination.</Text>
													{attribute.map((attr, index) => (
														<HStack key={index} spacing={3} align="flex-end">
															<FormControl>
																<FormLabel fontSize="xs" color="gray.600">Type</FormLabel>
																<Select size="sm" value={attr.attribute_name} onChange={(e) => handleChangeAttribute(index, 'attribute_name', e.target.value)} focusBorderColor="teal.400">
																	<option value="Color">Color</option>
																	<option value="Size">Size</option>
																</Select>
															</FormControl>
															<FormControl>
																<FormLabel fontSize="xs" color="gray.600">Value</FormLabel>
																<Input size="sm" value={attr.attribute_value} onChange={(e) => handleChangeAttribute(index, 'attribute_value', e.target.value)} focusBorderColor="teal.400" />
															</FormControl>
															<IconButton aria-label="Remove option" icon={<CloseIcon />} size="sm" variant="ghost" colorScheme="red" onClick={() => handleDeleteAttribute(index)} />
														</HStack>
													))}
													<HStack>
														<Button size="sm" variant="outline" colorScheme="teal" leftIcon={<AddIcon />} onClick={handleAddAttribute}>Add Option</Button>
														{attribute.length > 0 && <Button size="sm" colorScheme="purple" onClick={generateVariants}>Generate Variants</Button>}
													</HStack>
												</Stack>
											</Box>
										)}

										{variants.length > 0 && (
											<Box p={4} borderWidth="1px" borderRadius="md" borderColor="purple.200" bg="purple.50">
												<Text fontSize="sm" fontWeight="semibold" color="purple.700" mb={3}>Variant Pricing</Text>
												<Stack spacing={3}>
													{variants.map((variant, vIdx) => (
														<Box key={vIdx} p={3} bg="white" borderRadius="md" borderWidth="1px" borderColor="purple.100">
															<HStack justify="space-between" mb={2} flexWrap="wrap" spacing={3}>
																<Badge colorScheme="purple">{variant.label}</Badge>
																<Badge colorScheme={productDetails.out_of_stock || variant.out_of_stock ? 'red' : 'green'} fontSize="xs">
																	{productDetails.out_of_stock || variant.out_of_stock ? 'Out of Stock' : 'In Stock'}
																</Badge>
																<Checkbox
																	isChecked={!!variant.out_of_stock}
																	onChange={(e) => handleVariantOutOfStockChange(vIdx, e.target.checked)}
																	colorScheme="red"
																	size="sm"
																>
																	<Text fontSize="xs" color="red.500">Out of Stock</Text>
																</Checkbox>
																<HStack>
																	<Text fontSize="sm" color="gray.500">FB #</Text>
																	<Input size="sm" w="110px" placeholder="Part #" value={variant.fb_number || ''} onChange={(e) => handleVariantFbNumberChange(vIdx, e.target.value)} focusBorderColor="purple.400" />
																</HStack>
																<HStack>
																	<Text fontSize="sm" color="gray.500">Base $</Text>
																	<Input size="sm" w="90px" type="number" step="0.01" onWheel={(e) => e.currentTarget.blur()} placeholder="0.00" value={variant.price} onChange={(e) => handleVariantPriceChange(vIdx, e.target.value)} focusBorderColor="purple.400" />
																</HStack>
															</HStack>
															<Text fontSize="xs" color="gray.500" mb={2}>
																Fishbowl qty:{' '}
																<Text as="span" fontWeight="semibold" color="gray.700">
																	{variant.qty_available == null || variant.qty_available === ''
																		? '—'
																		: Number(variant.qty_available)}
																</Text>
																{' · '}
																Synced{' '}
																{variant.qty_synced_at
																	? new Date(variant.qty_synced_at).toLocaleString('en-US', {
																			month: 'short',
																			day: 'numeric',
																			hour: '2-digit',
																			minute: '2-digit',
																		})
																	: '—'}
															</Text>
															{variablePrice && facList.length > 0 && (
																<VStack spacing={1} align="stretch" pl={2}>
																	<Text fontSize="xs" color="gray.500" fontWeight="semibold">Facility overrides:</Text>
																	{facList.map((fac) => {
																		const facOverride = variant.facilityPrices?.[fac.facilityId];
																		const hasOverride = facOverride !== undefined && facOverride !== '' && facOverride !== null;
																		return (
																			<HStack key={fac.facilityId} spacing={2}>
																				<Text fontSize="xs" color="gray.600" minW="120px">{fac.name}</Text>
																				<Text fontSize="xs" color="gray.500">$</Text>
																				<Input size="xs" w="70px" type="number" step="0.01" onWheel={(e) => e.currentTarget.blur()} placeholder={`${variant.price || '0.00'} (default)`} value={facOverride ?? ''} onChange={(e) => handleVariantFacPriceChange(vIdx, fac.facilityId, e.target.value)} focusBorderColor="teal.400" />
																				{hasOverride && Number(facOverride) === 0 && <Badge colorScheme="orange" fontSize="xs">Not sold</Badge>}
																				{hasOverride && Number(facOverride) > 0 && <Badge colorScheme="blue" fontSize="xs">Override</Badge>}
																			</HStack>
																		);
																	})}
																</VStack>
															)}
															{priceGroups && priceGroups.length > 0 && (
																<VStack spacing={1} align="stretch" pl={2} mt={1}>
																	<Text fontSize="xs" color="gray.500" fontWeight="semibold">Price group overrides:</Text>
																	{priceGroups.map((group) => {
																		const groupOverride = variant.groupPrices?.[group.id];
																		const hasOverride = groupOverride !== undefined && groupOverride !== '' && groupOverride !== null;
																		return (
																			<HStack key={group.id} spacing={2}>
																				<Text fontSize="xs" color="gray.600" minW="120px">{group.name}</Text>
																				<Text fontSize="xs" color="gray.500">$</Text>
																				<Input size="xs" w="70px" type="number" step="0.01" onWheel={(e) => e.currentTarget.blur()} placeholder={`${variant.price || '0.00'} (default)`} value={groupOverride ?? ''} onChange={(e) => handleVariantGroupPriceChange(vIdx, group.id, e.target.value)} focusBorderColor="blue.400" />
																				{hasOverride && Number(groupOverride) === 0 && <Badge colorScheme="orange" fontSize="xs">Not sold</Badge>}
																				{hasOverride && Number(groupOverride) > 0 && <Badge colorScheme="blue" fontSize="xs">Override</Badge>}
																			</HStack>
																		);
																	})}
																</VStack>
															)}
														</Box>
													))}
												</Stack>
											</Box>
										)}

										<Divider />

										<Checkbox colorScheme="teal" isChecked={variablePrice} onChange={() => { if (variablePrice) setFacPrices([]); setVariablePrice(!variablePrice); }}>
											<Text fontSize="sm">This product has different prices per facility</Text>
										</Checkbox>

										{variablePrice && variants.length === 0 && (
											<Stack spacing={3}>
												<ChakraAlert status="info" borderRadius="md" fontSize="sm"><AlertIcon />Facility prices override the default product price.</ChakraAlert>
												{facListLoading ? <Text fontSize="sm" color="gray.500">Loading facilities...</Text> : facList.map((fac) => {
													const facPrice = facPrices.find((p) => p.facilityId === fac.facilityId);
													return (
														<Card key={fac.facilityId} variant="outline" size="sm">
															<Flex alignItems="center" px={4} py={3} justify="space-between">
																<Text fontSize="sm" fontWeight="medium" flex="1">{fac.name}</Text>
																<HStack>
																	<Text fontSize="sm" color="gray.500">$</Text>
																	<Input
																		w="100px"
																		size="sm"
																		type="number"
																		step="0.01"
																		placeholder={`${productDetails.price ?? '0.00'} (default)`}
																		value={facPrice?.price ?? ''}
																		onChange={(e) => handlePriceChange(fac.facilityId, e.target.value)}
																		focusBorderColor="teal.400"
																		onWheel={(e) => e.currentTarget.blur()}
																	/>
																	{facPrice && Number(facPrice.price) === 0 && (
																		<Badge colorScheme="orange" fontSize="xs">Not sold</Badge>
																	)}
																	{facPrice && Number(facPrice.price) > 0 && (
																		<Badge colorScheme="blue" fontSize="xs">Override</Badge>
																	)}
																</HStack>
															</Flex>
														</Card>
													);
												})}
											</Stack>
										)}
									</Stack>
								</SectionCard>

								{priceGroups && priceGroups.length > 0 && variants.length === 0 && (
									<SectionCard title="Price Group Pricing">
										<Stack spacing={3}>
											<Text fontSize="xs" color="blue.500" fontWeight="medium">Set a price for each group. Leave blank to use the product default price.</Text>
											{priceGroups.map((group) => {
												const current = groupPrices.find((g) => g.groupId === group.id);
												return (
													<Card key={group.id} variant="outline" size="sm" bg={current ? 'blue.50' : 'transparent'}>
														<Flex alignItems="center" px={4} py={3} justify="space-between">
															<Text fontSize="sm" fontWeight="medium" flex="1">{group.name}</Text>
															<HStack>
																<Text fontSize="sm" color="gray.500">$</Text>
																<Input w="100px" size="sm" type="number" step="0.01" onWheel={(e) => e.currentTarget.blur()} placeholder={productDetails.price || '0.00'} value={current?.price ?? ''} onChange={(e) => handleGroupPriceChange(group.id, e.target.value)} focusBorderColor="blue.400" />
															</HStack>
														</Flex>
													</Card>
												);
											})}
										</Stack>
									</SectionCard>
								)}
							</Stack>
						</form>
					</GridItem>

					{/* Preview Panel */}
					<GridItem>
						<Box
							p={4}
							bg="white"
							boxShadow="sm"
							borderRadius="lg"
							borderWidth="1px"
							borderColor="gray.200"
							position="sticky"
							top="80px"
						>
							<HStack spacing={2} mb={3}>
								<ViewIcon color="gray.500" />
								<Text fontSize="sm" fontWeight="semibold" color="gray.600">Live Preview</Text>
							</HStack>
							<Divider mb={3} />
							<ProductPreviewCard
								title={productDetails.name}
								image={productDetails.image}
								price={productDetails.price}
								description={productDetails.description}
								attribute={attribute}
								variants={variants}
								photo={previewPhoto}
								unit={productDetails.unit}
								quantityPerUnit={productDetails.quantity_per_unit}
							/>
						</Box>
					</GridItem>
				</Grid>
			</Box>

			<Modal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} size="lg" isCentered scrollBehavior="inside">
				<ModalOverlay />
				<ModalContent maxH="85vh">
					<ModalHeader>Preview Changes</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Stack spacing={4}>
							{changes.photoChanged && (
								<Box>
									<Badge colorScheme="purple" mb={2}>Photo updated</Badge>
									<Text fontSize="sm" color="gray.600">A new product image will be uploaded.</Text>
								</Box>
							)}

							{(changes.attributesChanged || changes.facPricesChanged || changes.groupPricesChanged) && (
								<HStack spacing={2} flexWrap="wrap">
									{changes.attributesChanged && <Badge colorScheme="cyan">Attributes updated</Badge>}
									{changes.facPricesChanged && <Badge colorScheme="blue">Facility prices updated</Badge>}
									{changes.groupPricesChanged && <Badge colorScheme="teal">Group prices updated</Badge>}
								</HStack>
							)}

							{changes.fieldChanges.length > 0 && (
								<Box>
									<Text fontWeight="semibold" mb={2} fontSize="sm" color="gray.600">Details</Text>
									<Box border="1px solid" borderColor="gray.100" borderRadius="md" overflow="hidden">
										{changes.fieldChanges.map((c) => (
											<Flex key={c.label} px={3} py={2} justify="space-between" gap={3} borderBottom="1px solid" borderColor="gray.50" fontSize="sm">
												<Text color="gray.500" minW="110px">{c.label}</Text>
												<HStack spacing={1} flex={1} justify="flex-end" flexWrap="wrap">
													<Text color="gray.400" textDecoration="line-through">{c.from}</Text>
													<Text>→</Text>
													<Text fontWeight="semibold">{c.to}</Text>
												</HStack>
											</Flex>
										))}
									</Box>
								</Box>
							)}

							{changes.variantsAdded.length > 0 && (
								<Box>
									<HStack mb={2}>
										<Badge colorScheme="green">Variants added</Badge>
										<Text fontSize="sm" color="gray.500">{changes.variantsAdded.length}</Text>
									</HStack>
									<Box border="1px solid" borderColor="green.100" borderRadius="md" overflow="hidden">
										{changes.variantsAdded.map((v, i) => (
											<Flex key={`${v.label}-${i}`} px={3} py={2} justify="space-between" bg="green.50" borderBottom="1px solid" borderColor="green.50">
												<Text fontSize="sm">{v.label || 'Variant'}</Text>
												<Text fontSize="sm" fontWeight="semibold">${(normPrice(v.price) ?? 0).toFixed(2)}</Text>
											</Flex>
										))}
									</Box>
								</Box>
							)}

							{changes.variantsRemoved.length > 0 && (
								<Box>
									<HStack mb={2}>
										<Badge colorScheme="red">Variants removed</Badge>
										<Text fontSize="sm" color="gray.500">{changes.variantsRemoved.length}</Text>
									</HStack>
									<Box border="1px solid" borderColor="red.100" borderRadius="md" overflow="hidden">
										{changes.variantsRemoved.map((v, i) => (
											<Flex key={`${v.label}-${i}`} px={3} py={2} justify="space-between" bg="red.50" borderBottom="1px solid" borderColor="red.50">
												<Text fontSize="sm">{v.label || 'Variant'}</Text>
												<Text fontSize="sm" color="gray.500">${(normPrice(v.price) ?? 0).toFixed(2)}</Text>
											</Flex>
										))}
									</Box>
								</Box>
							)}

							{changes.variantsPriceChanged.length > 0 && (
								<Box>
									<HStack mb={2}>
										<Badge colorScheme="orange">Variant prices changed</Badge>
										<Text fontSize="sm" color="gray.500">{changes.variantsPriceChanged.length}</Text>
									</HStack>
									<Box border="1px solid" borderColor="orange.100" borderRadius="md" overflow="hidden">
										{changes.variantsPriceChanged.map((v, i) => (
											<Flex key={`${v.label}-${i}`} px={3} py={2} justify="space-between" align="center" gap={3} bg="orange.50" borderBottom="1px solid" borderColor="orange.50">
												<Text fontSize="sm" flex={1}>{v.label}</Text>
												<HStack spacing={1} fontSize="sm">
													<Text color="gray.400" textDecoration="line-through">${(v.oldPrice ?? 0).toFixed(2)}</Text>
													<Text>→</Text>
													<Text fontWeight="semibold">${(v.newPrice ?? 0).toFixed(2)}</Text>
												</HStack>
											</Flex>
										))}
									</Box>
								</Box>
							)}

							{changes.variantGroupOverridesChanged.length > 0 && (
								<Box>
									<HStack mb={2}>
										<Badge colorScheme="teal">Variant group overrides</Badge>
										<Text fontSize="sm" color="gray.500">{changes.variantGroupOverridesChanged.length}</Text>
									</HStack>
									<Box border="1px solid" borderColor="teal.100" borderRadius="md" overflow="hidden">
										{changes.variantGroupOverridesChanged.map((v, i) => {
											const groupName = priceGroups?.find((g) => String(g.id) === String(v.groupId))?.name || `Group ${v.groupId}`;
											return (
												<Flex key={`${v.label}-${v.groupId}-${i}`} px={3} py={2} justify="space-between" align="center" gap={3} bg="teal.50" borderBottom="1px solid" borderColor="teal.50">
													<Box flex={1}>
														<Text fontSize="sm">{v.label}</Text>
														<Text fontSize="xs" color="gray.500">{groupName}</Text>
													</Box>
													<HStack spacing={1} fontSize="sm">
														<Text color="gray.400" textDecoration="line-through">{v.oldPrice == null ? '—' : `$${v.oldPrice.toFixed(2)}`}</Text>
														<Text>→</Text>
														<Text fontWeight="semibold">{v.newPrice == null ? '—' : `$${v.newPrice.toFixed(2)}`}</Text>
													</HStack>
												</Flex>
											);
										})}
									</Box>
								</Box>
							)}

							{changes.variantFacOverridesChanged.length > 0 && (
								<Box>
									<HStack mb={2}>
										<Badge colorScheme="blue">Variant facility overrides</Badge>
										<Text fontSize="sm" color="gray.500">{changes.variantFacOverridesChanged.length}</Text>
									</HStack>
									<Box border="1px solid" borderColor="blue.100" borderRadius="md" overflow="hidden">
										{changes.variantFacOverridesChanged.map((v, i) => {
											const facName = facList?.find((f) => String(f.facilityId) === String(v.facilityId))?.facilityName || `Facility ${v.facilityId}`;
											return (
												<Flex key={`${v.label}-${v.facilityId}-${i}`} px={3} py={2} justify="space-between" align="center" gap={3} bg="blue.50" borderBottom="1px solid" borderColor="blue.50">
													<Box flex={1}>
														<Text fontSize="sm">{v.label}</Text>
														<Text fontSize="xs" color="gray.500">{facName}</Text>
													</Box>
													<HStack spacing={1} fontSize="sm">
														<Text color="gray.400" textDecoration="line-through">{v.oldPrice == null ? '—' : `$${v.oldPrice.toFixed(2)}`}</Text>
														<Text>→</Text>
														<Text fontWeight="semibold">{v.newPrice == null ? '—' : `$${v.newPrice.toFixed(2)}`}</Text>
													</HStack>
												</Flex>
											);
										})}
									</Box>
								</Box>
							)}

							{!changes.hasChanges && (
								<Text fontSize="sm" color="gray.400">No changes detected.</Text>
							)}
						</Stack>
					</ModalBody>
					<ModalFooter borderTop="1px solid" borderColor="gray.100">
						<Button variant="ghost" mr={3} onClick={() => setIsPreviewOpen(false)}>
							Back to Edit
						</Button>
						<Button colorScheme="teal" onClick={handleConfirmSave} isLoading={isSubmitting}>
							Confirm Save
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

const mapStateToProps = (state) => ({
	product: state.adminProducts.product,
	productLoading: state.adminProducts.productLoading,
	categories: state.categories.categories,
	catloading: state.categories.loading,
	facListLoading: state.facilities.facListLoading,
	facList: state.facilities.facList,
	priceGroups: state.priceGroups.groups,
});

const mapDispatchToProps = {
	getProductByID,
	fetchStart,
	getAllCategories,
	getAllFacilityList,
	updateProduct,
	getAllPriceGroups,
};

export default connect(mapStateToProps, mapDispatchToProps)(Editproduct);
